import { Box, Button, IconButton, Toolbar } from "@mui/material";
import SearchInput from "../../tenant/search-input/search-input";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import styles from "./faq-manage.module.scss";
import { useTranslation } from "react-i18next";
import ResourceEditModal, {
  ResourceFormType,
} from "../resource-edit-modal/resource-edit-modal";
import DeleteResourceModal from "../delete-resource-modal/delete-resource-modal";
import {
  createResource,
  updateResource,
  deleteResource,
  getResourceList,
} from "api/layer";
import {
  FaqType,
  FaqDetail,
  FaqListItem,
  useFileDownload,
  getFileIcon,
  getFileExtension,
  getDisplayName,
} from "components/faq";

// Map API response to FaqType
const mapApiToFaq = (item: any): FaqType => {
  const attachments = item.attachments || [];
  const files = Array.isArray(attachments)
    ? attachments.map((attachment: string, index: number) => {
        const fileName = attachment.split("/").pop() || attachment;
        return {
          id: `f${index + 1}`,
          name: getDisplayName(attachment),
          size: "",
          type: getFileExtension(fileName),
          url: attachment,
        };
      })
    : undefined;

  return {
    id: String(item.resourceId),
    version: item.resourceVersion || "",
    title: item.resourceTitle || "",
    content: item.resourceContent || "",
    attachments: attachments,
    files,
    status: item.status || "draft",
  };
};

function FaqManage() {
  const [context, dispatch] = useContext(AppContext);
  const { t } = useTranslation();

  const [faqs, setFaqs] = useState<FaqType[]>([]);
  const fetchFaqList = async (): Promise<FaqType[] | undefined> => {
    try {
      const res: any = await getResourceList({ keyword: searchKeyword });
      const details = res?.data?.releaseDetails || res?.releaseDetails || [];
      if (!Array.isArray(details)) return;
      // Filter only "FAQ" type resources
      const faqDetails = details.filter(
        (item: any) => item.resourceType === "FAQ"
      );
      const mapped = faqDetails.map(mapApiToFaq);
      setFaqs(mapped);
      return mapped;
    } catch (error) {
      console.error("Failed to fetch FAQs:", error);
      return undefined;
    }
  };

  const [selectedFaq, setSelectedFaq] = useState<FaqType | null>(null);
  const [open, setOpen] = useState(false);
  const [currentFaq, setCurrentFaq] = useState<FaqType | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const { downloadingFile, handleDownloadFile } = useFileDownload({ dispatch });

  const filteredFaqs = faqs;

  useEffect(() => {
    fetchFaqList();
  }, [searchKeyword]);

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setShowFilters(false);
  };

  const handleFaqClick = (faq: FaqType) => {
    setSelectedFaq(faq);
  };

  const handleBackToList = () => {
    setSelectedFaq(null);
  };

  const handleSave = async (data: ResourceFormType) => {
    const payload: any = {
      resourceType: "FAQ",
      resourceVersion: data.version,
      resourceTitle: data.title,
      resourceContent: data.content,
      attachments: data.attachments || [],
      status: data.status || "draft",
    };

    try {
      if (currentFaq) {
        payload.resourceId = Number(data.id);
        await updateResource(payload);
      } else {
        await createResource(payload);
      }
      const updated = await fetchFaqList();
      if (selectedFaq && selectedFaq.id === data.id && updated) {
        const refreshed = updated.find((r) => r.id === data.id);
        if (refreshed) setSelectedFaq(refreshed);
      }
    } catch (error) {
      console.error("Failed to save FAQ:", error);
    } finally {
      setOpen(false);
      setCurrentFaq(null);
    }
  };

  const handleDelete = (faq: FaqType) => {
    setCurrentFaq(faq);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!currentFaq) return;
    try {
      await deleteResource(Number(currentFaq.id));
      await fetchFaqList();
      if (selectedFaq && selectedFaq.id === currentFaq.id) {
        setSelectedFaq(null);
      }
    } catch (error) {
      console.error("Failed to delete FAQ:", error);
    } finally {
      setCurrentFaq(null);
      setOpenDeleteModal(false);
    }
  };

  if (selectedFaq) {
    const adminActions = context.menu?.find((item) => item.id === "100002") ? (
      <div className="flex gap-2 items-center">
        <IconButton
          onClick={() => {
            setCurrentFaq(selectedFaq);
            setOpen(true);
          }}
          className="!text-[#00463B]"
          aria-label="edit"
        >
          <span className="material-icons">edit</span>
        </IconButton>
        <IconButton
          onClick={() => handleDelete(selectedFaq)}
          className="!text-[#d32f2f]"
          aria-label="delete"
        >
          <span className="material-icons">delete</span>
        </IconButton>
      </div>
    ) : null;

    return (
      <>
        <FaqDetail
          faq={selectedFaq}
          downloadingFile={downloadingFile}
          onDownloadFile={handleDownloadFile}
          attachmentsLabel={t("faq.attachments")}
          containerClassName={styles.container}
          actionsContent={
            <div className="mb-6 flex justify-between items-center w-full">
              <IconButton
                onClick={handleBackToList}
                className="!text-[#00463B]"
                aria-label="back"
              >
                <span className="material-icons text-[24px]">arrow_back</span>
                <span className="ml-2 text-base">{t("buttons.back")}</span>
              </IconButton>
              {adminActions}
            </div>
          }
        />

        {open && (
          <ResourceEditModal
            open={open}
            resourceType="faq"
            onClose={() => {
              setOpen(false);
              setCurrentFaq(null);
            }}
            onSave={handleSave}
            data={currentFaq || undefined}
            fileIconGetter={getFileIcon}
          />
        )}

        {openDeleteModal && currentFaq && (
          <DeleteResourceModal
            data={currentFaq}
            resourceType="faq"
            onConfirm={confirmDelete}
            onClose={() => {
              setOpenDeleteModal(false);
              setCurrentFaq(null);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className={styles.container}>
      <Toolbar
        style={{
          justifyContent: "space-between",
          borderBottom: "1px solid #ccc",
          marginBottom: "5px",
          padding: "0",
        }}
      >
        {context.menu && context.menu.find((item) => item.id === "100002") && (
          <Button
            variant="contained"
            color="success"
            size="small"
            endIcon={<span className="material-icons">add</span>}
            onClick={() => {
              setOpen(true);
              setCurrentFaq(null);
            }}
          >
            {t("userSystem.addFaq")}
          </Button>
        )}
        <IconButton
          color="success"
          onClick={toggleFilters}
          style={{
            color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
            background: showFilters ? "#c1e5ca" : "",
          }}
        >
          <span className="material-icons">filter_list</span>
        </IconButton>
      </Toolbar>

      {showFilters && (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#f5f5f5",
            justifyContent: "space-between",
          }}
        >
          <Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
            <SearchInput onSearch={handleSearch} />
          </Box>
          <IconButton onClick={clearFilters} aria-label="clear" color="default">
            <span className="material-icons">clear</span>
          </IconButton>
        </Box>
      )}

      <div style={{ flex: 0.95, overflow: "auto" }}>
        <div className="w-full h-auto py-4 flex flex-col gap-6">
          {filteredFaqs.map((faq) => (
            <FaqListItem
              key={faq.id}
              faq={faq}
              onClick={handleFaqClick}
              className="w-full p-4 border-y border-[#bbb] relative cursor-pointer hover:bg-gray-50 transition-colors"
            />
          ))}
        </div>
      </div>

      {open && (
        <ResourceEditModal
          open={open}
          resourceType="faq"
          onClose={() => {
            setOpen(false);
            setCurrentFaq(null);
          }}
          onSave={handleSave}
          data={currentFaq || undefined}
          fileIconGetter={getFileIcon}
        />
      )}

      {openDeleteModal && currentFaq && (
        <DeleteResourceModal
          data={currentFaq}
          resourceType="faq"
          onConfirm={confirmDelete}
          onClose={() => {
            setOpenDeleteModal(false);
            setCurrentFaq(null);
          }}
        />
      )}
    </div>
  );
}

export default FaqManage;
